import React, { useState, useMemo } from "react";
import {
  Box,
  Grid,
  Stack,
  Typography,
  Button,
  useTheme,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PersonIcon from "@mui/icons-material/Person";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import BedtimeIcon from "@mui/icons-material/Bedtime";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";

import {
  GaugeChart,
  LineChart,
  RadarChart,
  CircularProgressIndicator,
  RiskBreakdownChart,
  HorizontalBarChart,
} from "../../../../../shared/components/charts";
import {
  DashboardCard,
  DashboardCardSkeleton,
  CohortGuard,
  FilterDrawer,
  FilterState,
  getAppliedFilterCount,
  DurationSelector,
  DateRange,
} from "../../../../../shared/components";
import { TextStat } from "../../../../../shared/components/charts/TextStat";
import { formatTrendPercent } from "../../../../../shared/utils";
import {
  useWellnessOverviewStats,
  useRecoveryStrainTrends,
  useSleepTrends,
  useHRVTrends,
  useWorkoutTimeTrends,
  useStrainActivityByDepartment,
  useSleepQualityByDepartment,
  useWorkoutActivityByDepartment,
  useWellbeingDistribution,
} from "../hooks/useWellnessOverview";
import {
  buildFilterParams,
  transformTrendsData,
  calculateGranularity,
  roundToDecimal,
} from "../../shared/utils";
import { useClientScope } from "../../../../../shared/contexts";
import {
  usePersistedDateRange,
  usePersistedFilters,
  useMetricsOverallRange,
} from "../../../../../shared/hooks";

const WELLNESS_OVERVIEW_DETAILS = {
  averageHrv: `Heart rate variability (HRV) refers to the variation in time between successive heartbeats and is a key indicator of autonomic nervous system regulation and recovery status. Higher HRV generally reflects greater parasympathetic (rest-and-digest) activity and adaptive capacity.

The gauge compares current HRV to an individualised baseline derived from prior measurements. Values below baseline may indicate elevated sympathetic activation, stress, or fatigue, while values within or above baseline suggest adequate recovery and balanced autonomic function.`,
  overallEmployeeWellness: `Provides a summary view of three core physiological metrics: recovery, strain, and sleep. Recovery reflects autonomic readiness and restorative status, strain represents cumulative physiological load, and sleep captures both duration and quality of rest.

Together, these measures offer a structured overview of workload balance and recovery adequacy, highlighting broader wellbeing trends and potential areas of sustained stress or resilience across the workforce.`,
  riskBreakdown: `Displays the number of active employees within each predefined risk band (e.g., low, moderate, high), alongside the overall participation rate. Risk bands are typically derived from aggregated physiological and recovery-related indicators, reflecting relative wellbeing status.

This distribution view helps identify the proportion of the workforce potentially at elevated risk, monitor shifts in risk profile over time, and assess engagement levels to ensure data representativeness and programme impact.`,
  strainAndRecovery: `Displays the daily trend of recovery relative to strain, illustrating the relationship between physiological load and restorative capacity over time. Strain reflects cumulative cardiovascular and metabolic demand from activity and stress, while recovery indicates autonomic balance and readiness.

Tracking these metrics together helps assess whether workload is appropriately balanced with adequate recovery, identify periods of sustained high load, and monitor potential fatigue accumulation across the organisation.`,
  organisationHealthOverview: `Provides a benchmark comparison of company-wide health across key physiological metrics, such as recovery, strain, sleep, HRV, and resting heart rate. This overview enables evaluation against defined internal standards, historical baselines, or relevant peer groups.

By aggregating these indicators, it offers a high-level view of overall workforce wellbeing, highlighting strengths, emerging risks, and areas requiring targeted intervention or support.`,
  workoutTime: `Displays organisation-wide trends in total workout duration over time, reflecting overall engagement in structured physical activity. This metric captures cumulative exercise time and highlights changes in participation levels and activity consistency across the workforce.

Monitoring these trends helps identify periods of increased or reduced exercise engagement, supporting assessment of physical activity habits and their potential impact on recovery, cardiovascular health, and overall wellbeing.`,
  averageSleepDuration: `Compares the average number of hours slept per night across the organisation, providing insight into overall sleep quantity and sufficiency. Sleep duration is a key determinant of cognitive performance, physiological recovery, and long-term health.

By analysing trends over time or differences between teams or groups, this metric helps identify patterns of sleep restriction, improvements in sleep behaviour, or variability linked to workload and lifestyle factors. Consistently low average sleep duration may indicate elevated fatigue risk and reduced recovery capacity across the workforce.`,
  departmentPerformance: `Displays average wellness metrics by department, segmented across strain, recovery, and activity. Strain reflects overall physiological load, recovery indicates readiness and autonomic balance, and activity captures movement volume and intensity.

This view enables comparison of workload, recovery capacity, and engagement patterns between teams, helping identify departments experiencing sustained high load, strong recovery balance, or differing activity behaviours.`,
};

export const WellnessOverview: React.FC = () => {
  const theme = useTheme();
  const { effectiveClientId, isCentralAdmin, isAnonymised } = useClientScope();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { filters, setFilters } = usePersistedFilters();
  const activeFiltersCount = getAppliedFilterCount(filters);
  const { dateRange, setDateRange } = usePersistedDateRange();
  const [departmentView, setDepartmentView] = useState(
    "strain_activity_by_department"
  );
  const riskLogicTooltip =
    "Risk score is calculated by averaging four banded risk components (Recovery, Sleep, Strain, HRV), then mapped to Low/Medium/High.";
  const renderTrendBadge = (change: string | undefined) => {
    const normalized = formatTrendPercent(change) ?? "";
    if (!normalized) return null;
    const isNeg = normalized.startsWith("-");
    const isPos = normalized.startsWith("+");
    const color = isNeg
      ? theme.palette.error.main
      : isPos
      ? theme.palette.success.main
      : theme.palette.text.secondary;
    const Icon = isNeg ? TrendingDownIcon : isPos ? TrendingUpIcon : TrendingFlatIcon;
    return (
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.25, color }}>
        <Typography component="span" sx={{ fontWeight: 600, fontSize: "0.8rem", color }}>
          {normalized}
        </Typography>
        <Icon sx={{ fontSize: "0.9rem", color }} />
      </Box>
    );
  };

  const getRecoverySleepGaugeColor = (value: number) => {
    if (value >= 80) return theme.palette.success.main;
    if (value >= 30) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  const filterParams = useMemo(
    () => buildFilterParams(filters, dateRange, effectiveClientId),
    [filters, dateRange, effectiveClientId]
  );

  const granularity = useMemo(
    () => calculateGranularity(dateRange),
    [dateRange]
  );

  const {
    data: statsData,
    isLoading: statsLoading,
    error: statsError,
  } = useWellnessOverviewStats(filterParams);

  const {
    data: recoveryStrainTrendsData,
    isLoading: recoveryStrainTrendsLoading,
    error: recoveryStrainTrendsError,
  } = useRecoveryStrainTrends({
    ...filterParams,
    metrics: ["recovery_score", "strain"],
    granularity,
  });

  const {
    data: sleepTrendsData,
    isLoading: sleepTrendsLoading,
    error: sleepTrendsError,
  } = useSleepTrends({
    ...filterParams,
    metrics: ["hours_of_sleep", "hours_of_sleep_needed"],
    granularity,
  });

  const {
    data: hrvTrendsData,
    isLoading: hrvTrendsLoading,
    error: hrvTrendsError,
  } = useHRVTrends({
    ...filterParams,
    metrics: ["hrv", "average_hrv"],
    granularity,
  });

  const {
    data: workoutTimeTrendsData,
    isLoading: workoutTimeTrendsLoading,
    error: workoutTimeTrendsError,
  } = useWorkoutTimeTrends({
    ...filterParams,
    metrics: ["workout_time"],
    granularity,
  });

  const {
    data: strainActivityByDepartmentData,
    isLoading: strainActivityByDepartmentLoading,
    error: strainActivityByDepartmentError,
  } = useStrainActivityByDepartment(filterParams);

  const {
    data: sleepQualityByDepartmentData,
    isLoading: sleepQualityByDepartmentLoading,
    error: sleepQualityByDepartmentError,
  } = useSleepQualityByDepartment(filterParams);

  const {
    data: workoutActivityByDepartmentData,
    isLoading: workoutActivityByDepartmentLoading,
    error: workoutActivityByDepartmentError,
  } = useWorkoutActivityByDepartment(filterParams);

  const {
    data: wellbeingDistributionData,
    isLoading: wellbeingDistributionLoading,
    error: wellbeingDistributionError,
  } = useWellbeingDistribution(filterParams);

  const overallRangeQuery = useMetricsOverallRange(effectiveClientId);

  const overallMinMax = useMemo(() => {
    const metric_min = overallRangeQuery.data?.data?.metric_min ?? {};
    const metric_max = overallRangeQuery.data?.data?.metric_max ?? {};
    return {
      recoveryMin: typeof metric_min.recovery_score === "number" ? metric_min.recovery_score : undefined,
      recoveryMax: typeof metric_max.recovery_score === "number" ? metric_max.recovery_score : undefined,
      strainMin: typeof metric_min.strain === "number" ? metric_min.strain : undefined,
      strainMax: typeof metric_max.strain === "number" ? metric_max.strain : undefined,
      workoutTimeMin: typeof metric_min.workout_time === "number" ? metric_min.workout_time : undefined,
      workoutTimeMax: typeof metric_max.workout_time === "number" ? metric_max.workout_time : undefined,
      hoursOfSleepMin: typeof metric_min.hours_of_sleep === "number" ? metric_min.hours_of_sleep : undefined,
      hoursOfSleepMax: typeof metric_max.hours_of_sleep === "number" ? metric_max.hours_of_sleep : undefined,
    };
  }, [overallRangeQuery.data]);

  // Active user count for cohort privacy guard.
  const activeUserCount = statsData?.data?.active_users?.value ?? statsData?.data?.total_active_employees?.value;

  const handleApplyFilters = (newFilters: FilterState) => {
    setFilters(newFilters);
  };

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range);
  };

  const strainRecoveryTrends = useMemo(() => {
    if (!recoveryStrainTrendsData) return [];
    return transformTrendsData(recoveryStrainTrendsData, [
      "recovery_score",
      "strain",
    ]);
  }, [recoveryStrainTrendsData]);

  const sleepTrends = useMemo(() => {
    if (!sleepTrendsData) return [];
    return transformTrendsData(sleepTrendsData, [
      "hours_of_sleep",
      "hours_of_sleep_needed",
    ]);
  }, [sleepTrendsData]);

  const hrvTrends = useMemo(() => {
    if (!hrvTrendsData) return [];
    return transformTrendsData(hrvTrendsData, ["hrv", "average_hrv"]);
  }, [hrvTrendsData]);

  const workoutTimeTrends = useMemo(() => {
    if (!workoutTimeTrendsData) return [];
    return transformTrendsData(workoutTimeTrendsData, ["workout_time"]);
  }, [workoutTimeTrendsData]);

  const organizationHealthData = useMemo(() => {
    if (!wellbeingDistributionData?.data?.distribution) return [];
    return wellbeingDistributionData.data.distribution.map((item) => ({
      metric: item.label,
      currentScore: roundToDecimal(item.value),
      optimalTarget: 100,
    }));
  }, [wellbeingDistributionData]);

  const departmentPerformanceData = useMemo(() => {
    const dataMap: Record<string, Array<{ label: string; value: number }>> = {};

    if (strainActivityByDepartmentData?.data?.distribution) {
      dataMap.strain_activity_by_department =
        strainActivityByDepartmentData.data.distribution.map((item) => ({
          label: item.label,
          value: roundToDecimal(item.value),
        }));
    }

    if (sleepQualityByDepartmentData?.data?.distribution) {
      dataMap.sleep_quality_by_department =
        sleepQualityByDepartmentData.data.distribution.map((item) => ({
          label: item.label,
          value: roundToDecimal(item.value),
        }));
    }

    if (workoutActivityByDepartmentData?.data?.distribution) {
      dataMap.workout_activity_by_department =
        workoutActivityByDepartmentData.data.distribution.map((item) => ({
          label: item.label,
          value: roundToDecimal(item.value),
        }));
    }

    return dataMap;
  }, [
    strainActivityByDepartmentData,
    sleepQualityByDepartmentData,
    workoutActivityByDepartmentData,
  ]);

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <DurationSelector
              value={dateRange}
              onChange={handleDateRangeChange}
              buttonVariant="outlined"
            />
            <Button
              variant="text"
              size="medium"
              startIcon={<FilterListIcon />}
              color={activeFiltersCount > 0 ? "primary" : "inherit"}
              onClick={() => setIsFilterOpen(true)}
            >
              {activeFiltersCount > 0 ? `FILTER (${activeFiltersCount})` : "FILTER"}
            </Button>
          </Box>
        </Box>

        <FilterDrawer
          open={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          onApply={handleApplyFilters}
          initialFilters={filters}
        />

        {statsError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load wellness statistics. Please try again.
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            {statsLoading ? (
              <DashboardCardSkeleton title subtitle height={200} />
            ) : (
              <DashboardCard
                title={
                  statsData?.data?.average_hrv?.label ||
                  statsData?.data?.hrv?.label ||
                  "Average HRV"
                }
                subtitle="Gauge shows how current HRV compares to baseline range."
                details={WELLNESS_OVERVIEW_DETAILS.averageHrv}
                titleComponent={
                  (() => {
                    const hrv = statsData?.data?.average_hrv ?? statsData?.data?.hrv;
                    return hrv ? (
                      <TextStat
                        title="Organisation Average"
                        value={`${roundToDecimal(hrv.value)}${hrv.unit || "ms"}`}
                        change={hrv.change}
                      />
                    ) : undefined;
                  })()
                }
              >
                <CohortGuard
                  userCount={activeUserCount}
                  bypass={isCentralAdmin || !isAnonymised}
                  loading={statsLoading}
                >
                  <GaugeChart
                    value={roundToDecimal(
                      statsData?.data?.average_hrv?.value ||
                        statsData?.data?.hrv?.value ||
                        0
                    )}
                    min={0}
                    max={100}
                    baseline={(() => {
                      const baseline =
                        (statsData?.data?.average_hrv?.additional_fields
                          ?.baseline as { min: number; max: number }) ||
                        (statsData?.data?.hrv?.additional_fields?.baseline as {
                          min: number;
                          max: number;
                        });
                      if (baseline) {
                        return {
                          min: roundToDecimal(baseline.min),
                          max: roundToDecimal(baseline.max),
                        };
                      }
                      return undefined;
                    })()}
                    unit={
                      statsData?.data?.average_hrv?.unit ||
                      statsData?.data?.hrv?.unit ||
                      "ms"
                    }
                    height={200}
                    title={
                      statsData?.data?.average_hrv?.label ||
                      statsData?.data?.hrv?.label ||
                      "Average HRV"
                    }
                    useNeutralColors={true}
                  />
                </CohortGuard>
              </DashboardCard>
            )}
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            {statsLoading ? (
              <DashboardCardSkeleton title subtitle height={200} />
            ) : (
              <DashboardCard
                title="Overall Employee Wellness"
                tooltip="Employee wellness metrics overview"
                details={WELLNESS_OVERVIEW_DETAILS.overallEmployeeWellness}
              >
                <CohortGuard
                  userCount={activeUserCount}
                  bypass={isCentralAdmin || !isAnonymised}
                  loading={statsLoading}
                >
                  {(() => {
                    const recoveryValue = roundToDecimal(
                      statsData?.data?.recovery?.value ||
                        statsData?.data?.recovery_score?.value ||
                        0
                    );
                    const sleepValue = roundToDecimal(
                      statsData?.data?.sleep_performance?.value || 0
                    );

                    return (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-around",
                          alignItems: "flex-start",
                          flexWrap: "wrap",
                          gap: 3,
                          py: 2,
                        }}
                      >
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                          <CircularProgressIndicator
                            value={recoveryValue}
                            label="RECOVERY"
                            icon={<PersonIcon fontSize="small" />}
                            color={getRecoverySleepGaugeColor(recoveryValue)}
                            unit={
                              statsData?.data?.recovery?.unit ||
                              statsData?.data?.recovery_score?.unit ||
                              "%"
                            }
                          />
                          {renderTrendBadge(statsData?.data?.recovery?.change ?? statsData?.data?.recovery_score?.change)}
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                          <CircularProgressIndicator
                            value={roundToDecimal(statsData?.data?.strain?.value || 0)}
                            label="STRAIN"
                            icon={<DirectionsRunIcon fontSize="small" />}
                            color={theme.palette.info.main}
                            max={roundToDecimal(
                              (statsData?.data?.strain?.additional_fields?.max as
                                | number
                                | undefined) || 21
                            )}
                            showExtraTrack={true}
                            extraTrackValue={roundToDecimal(
                              (statsData?.data?.strain?.additional_fields?.target as
                                | number
                                | undefined) || 15
                            )}
                            extraTrackColor={theme.palette.action.selected}
                          />
                          {renderTrendBadge(statsData?.data?.strain?.change)}
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                          <CircularProgressIndicator
                            value={sleepValue}
                            label="SLEEP"
                            icon={<BedtimeIcon fontSize="small" />}
                            color={getRecoverySleepGaugeColor(sleepValue)}
                            unit={
                              statsData?.data?.sleep_performance?.unit || "%"
                            }
                          />
                          {renderTrendBadge(statsData?.data?.sleep_performance?.change)}
                        </Box>
                      </Box>
                    );
                  })()}
                </CohortGuard>
              </DashboardCard>
            )}
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            {statsLoading ? (
              <DashboardCardSkeleton title subtitle height={200} />
            ) : (
              <DashboardCard
                title="Risk breakdown"
                subtitle="Counts of active employees by risk band and participation rate."
                tooltip={riskLogicTooltip}
                details={WELLNESS_OVERVIEW_DETAILS.riskBreakdown}
              >
                <CohortGuard
                  userCount={activeUserCount}
                  bypass={isCentralAdmin || !isAnonymised}
                  loading={statsLoading}
                >
                  {(() => {
                    const totalActive =
                      statsData?.data?.total_active_employees?.value || 0;
                    const highRiskPercent = roundToDecimal(
                      statsData?.data?.high_risk_employees?.value || 0
                    );
                    const mediumRiskPercent = roundToDecimal(
                      statsData?.data?.medium_risk_employees?.value || 0
                    );
                    const lowRiskPercent = roundToDecimal(
                      statsData?.data?.low_risk_employees?.value || 0
                    );

                    const highRiskCount = Math.round(
                      (totalActive * highRiskPercent) / 100
                    );
                    const mediumRiskCount = Math.round(
                      (totalActive * mediumRiskPercent) / 100
                    );
                    const lowRiskCount = Math.round(
                      (totalActive * lowRiskPercent) / 100
                    );

                    return (
                      <RiskBreakdownChart
                        data={{
                          high: highRiskCount,
                          medium: mediumRiskCount,
                          low: lowRiskCount,
                        }}
                        total={totalActive}
                        participationRate={roundToDecimal(
                          statsData?.data?.wellness_participation_rate?.value ||
                            statsData?.data?.participation_rate?.value ||
                            0
                        )}
                        title={
                          statsData?.data?.total_active_employees?.label ||
                          "Total Active Employees"
                        }
                      />
                    );
                  })()}
                </CohortGuard>
              </DashboardCard>
            )}
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 8 }}>
            {recoveryStrainTrendsLoading ? (
              <DashboardCardSkeleton title subtitle height={350} />
            ) : recoveryStrainTrendsError ? (
              <DashboardCard
                title="Strain & Recovery "
                subtitle="Daily trend of recovery versus strain"
                details={WELLNESS_OVERVIEW_DETAILS.strainAndRecovery}
              >
                <Alert severity="error">
                  Failed to load recovery and strain trends.
                </Alert>
              </DashboardCard>
            ) : (
              <DashboardCard
                title="Strain & Recovery "
                subtitle="Daily trend of recovery versus strain"
                tooltip="Daily trend of recovery versus strain"
                details={WELLNESS_OVERVIEW_DETAILS.strainAndRecovery}
                titleComponent={
                  statsData?.data?.recovery_score ? (
                    <TextStat
                      title="Organisation Average"
                      value={`${roundToDecimal(
                        statsData.data.recovery_score.value
                      )}${statsData.data.recovery_score.unit || ""}`}
                      change={statsData.data.recovery_score.change}
                    />
                  ) : undefined
                }
              >
                <CohortGuard
                  userCount={activeUserCount}
                  bypass={isCentralAdmin || !isAnonymised}
                  loading={statsLoading}
                >
                  {strainRecoveryTrends.length > 0 ? (
                    <>
                      <LineChart
                        data={strainRecoveryTrends}
                        series={[
                          {
                            dataKey: "recovery_score",
                            label: "Recovery",
                            color: theme.palette.success.main,
                            area: true,
                          },
                          {
                            dataKey: "strain",
                            label: "Strain",
                            color: theme.palette.warning.main,
                            area: true,
                          },
                        ]}
                        height={300}
                        yAxisMin={0}
                        yAxisMax={100}
                        showMinMaxLines
                        referenceMin={overallMinMax.recoveryMin}
                        referenceMax={overallMinMax.recoveryMax}
                      />
                      {statsData?.data?.recovery_score?.rag_insight && (
                        <Alert icon={<InfoOutlinedIcon />} severity="info">
                          {statsData.data.recovery_score.rag_insight}
                        </Alert>
                      )}
                    </>
                  ) : (
                    <Alert severity="info">No trend data available.</Alert>
                  )}
                </CohortGuard>
              </DashboardCard>
            )}
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            {wellbeingDistributionLoading ? (
              <DashboardCardSkeleton title subtitle height={300} />
            ) : wellbeingDistributionError ? (
              <DashboardCard
                title="Organisation Health Overview"
                subtitle="Benchmark of company health across key metrics"
                details={WELLNESS_OVERVIEW_DETAILS.organisationHealthOverview}
              >
                <Alert severity="error">
                  Failed to load organisation health data.
                </Alert>
              </DashboardCard>
            ) : (
              <DashboardCard
                title="Organisation Health Overview"
                subtitle="Benchmark of company health across key metrics"
                details={WELLNESS_OVERVIEW_DETAILS.organisationHealthOverview}
              >
                <CohortGuard
                  userCount={activeUserCount}
                  bypass={isCentralAdmin || !isAnonymised}
                  loading={statsLoading}
                >
                  <Box
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    {organizationHealthData.length > 0 ? (
                      <RadarChart
                        data={organizationHealthData}
                        seriesKeys={["currentScore", "optimalTarget"]}
                        seriesLabels={["Your Organisation", "Optimal Target"]}
                        metricKey="metric"
                        height={300}
                        max={100}
                        colors={[
                          theme.palette.warning.main,
                          theme.palette.success.main,
                        ]}
                      />
                    ) : (
                      <Alert severity="info">
                        No organisation health data available.
                      </Alert>
                    )}
                  </Box>
                </CohortGuard>
              </DashboardCard>
            )}
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            {workoutTimeTrendsLoading ? (
              <DashboardCardSkeleton title subtitle height={350} />
            ) : workoutTimeTrendsError ? (
              <DashboardCard
                title="Workout Time"
                subtitle="Organisation-wide workout time trends"
                details={WELLNESS_OVERVIEW_DETAILS.workoutTime}
              >
                <Alert severity="error">
                  Failed to load workout time trends.
                </Alert>
              </DashboardCard>
            ) : (
              <DashboardCard
                title="Workout Time"
                subtitle="Organisation-wide workout time trends"
                details={WELLNESS_OVERVIEW_DETAILS.workoutTime}
                titleComponent={
                  statsData?.data?.workout_time ? (
                    <TextStat
                      title="Organisation Average"
                      value={`${roundToDecimal(
                        statsData.data.workout_time.value
                      )}${statsData.data.workout_time.unit || ""}`}
                      change={statsData.data.workout_time.change}
                    />
                  ) : undefined
                }
              >
                <CohortGuard
                  userCount={activeUserCount}
                  bypass={isCentralAdmin || !isAnonymised}
                  loading={statsLoading}
                >
                  {workoutTimeTrends.length > 0 ? (
                    <LineChart
                      data={workoutTimeTrends}
                      series={[
                        {
                          dataKey: "workout_time",
                          label: "Workout Time",
                          color: theme.palette.info.main,
                          area: true,
                        },
                      ]}
                      height={300}
                      yAxisMin={0}
                      showMinMaxLines
                      referenceMin={overallMinMax.workoutTimeMin}
                      referenceMax={overallMinMax.workoutTimeMax}
                    />
                  ) : (
                    <Alert severity="info">
                      No workout time trend data available.
                    </Alert>
                  )}
                </CohortGuard>
              </DashboardCard>
            )}
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            {sleepTrendsLoading ? (
              <DashboardCardSkeleton title subtitle height={350} />
            ) : sleepTrendsError ? (
              <DashboardCard
                title="Average Sleep Duration"
                subtitle="Comparison between average hours of sleep"
                details={WELLNESS_OVERVIEW_DETAILS.averageSleepDuration}
              >
                <Alert severity="error">Failed to load sleep trends.</Alert>
              </DashboardCard>
            ) : (
              <DashboardCard
                title="Average Sleep Duration"
                subtitle="Comparison between average hours of sleep"
                details={WELLNESS_OVERVIEW_DETAILS.averageSleepDuration}
                titleComponent={
                  statsData?.data?.sleep_performance ? (
                    <TextStat
                      title="Organisation Average"
                      value={`${roundToDecimal(
                        statsData.data.sleep_performance.value
                      )}${statsData.data.sleep_performance.unit || ""}`}
                      change={statsData.data.sleep_performance.change}
                    />
                  ) : undefined
                }
              >
                <CohortGuard
                  userCount={activeUserCount}
                  bypass={isCentralAdmin || !isAnonymised}
                  loading={statsLoading}
                >
                  {sleepTrends.length > 0 ? (
                    <LineChart
                      data={sleepTrends}
                      series={[
                        {
                          dataKey: "hours_of_sleep",
                          label: "Hours of Sleep",
                          color: theme.palette.info.main,
                          area: true,
                        },
                        {
                          dataKey: "hours_of_sleep_needed",
                          label: "Hours of Sleep Needed",
                          color: theme.palette.success.main,
                          area: true,
                        },
                      ]}
                      height={300}
                      yAxisMin={0}
                      showMinMaxLines
                      referenceMin={overallMinMax.hoursOfSleepMin}
                      referenceMax={overallMinMax.hoursOfSleepMax}
                    />
                  ) : (
                    <Alert severity="info">No sleep trend data available.</Alert>
                  )}
                </CohortGuard>
              </DashboardCard>
            )}
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            {strainActivityByDepartmentLoading ||
            sleepQualityByDepartmentLoading ||
            workoutActivityByDepartmentLoading ? (
              <DashboardCardSkeleton title subtitle height={300} />
            ) : strainActivityByDepartmentError ||
              sleepQualityByDepartmentError ||
              workoutActivityByDepartmentError ? (
              <DashboardCard
                title="Department Performance"
                subtitle="Average wellness performance by department, segmented across strain, recovery, and activity"
                details={WELLNESS_OVERVIEW_DETAILS.departmentPerformance}
              >
                <Alert severity="error">
                  Failed to load department performance data.
                </Alert>
              </DashboardCard>
            ) : (
              <DashboardCard
                title="Department Performance"
                subtitle="Average wellness performance by department, segmented across strain, recovery, and activity"
                details={WELLNESS_OVERVIEW_DETAILS.departmentPerformance}
                titleComponent={
                  <ToggleButtonGroup
                    value={departmentView}
                    exclusive
                    onChange={(_, newValue) => {
                      if (newValue !== null) setDepartmentView(newValue);
                    }}
                    size="small"
                  >
                    <ToggleButton value="strain_activity_by_department">
                      Strain & Activity
                    </ToggleButton>
                    <ToggleButton value="sleep_quality_by_department">
                      Sleep Quality
                    </ToggleButton>
                    <ToggleButton value="workout_activity_by_department">
                      Workout Activity
                    </ToggleButton>
                  </ToggleButtonGroup>
                }
              >
                <CohortGuard
                  userCount={activeUserCount}
                  bypass={isCentralAdmin || !isAnonymised}
                  loading={statsLoading}
                >
                  {departmentPerformanceData[departmentView] &&
                  departmentPerformanceData[departmentView].length > 0 ? (
                    <HorizontalBarChart
                      data={departmentPerformanceData[departmentView]}
                      height={250}
                    />
                  ) : (
                    <Alert severity="info">
                      No department performance data available.
                    </Alert>
                  )}
                </CohortGuard>
              </DashboardCard>
            )}
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};
